import { createBrowserRouter, useLocation } from 'react-router';
import { LoaderFunctionArgs } from 'react-router';

import { checkUserIsConnectedOnStart } from '@/domain/auth/actions/check-user-connected-on-start.action';
import { runImpactCalculateFestivalCo2ForProject } from '@/domain/impact-calculate-festival/actions/run-impact-calculate-festival-co2-for-project.action';
import { runImpactCalculateFestivalEnvForProject } from '@/domain/impact-calculate-festival/actions/run-impact-calculate-festival-env-for-project.action';
import { runImpactCalculateFestivalEssForProject } from '@/domain/impact-calculate-festival/actions/run-impact-calculate-festival-ess-for-project.action';
import { getImpactCalculateVenueCo2ForProject } from '@/domain/impact-calculate-venue/actions/get-impact-calculate-venue-co2-for-project.action';
import { getImpactCo2Answers } from '@/domain/impact-co2/actions/get-impact-co2-answers.action';
import { getImpactEnvQuestions } from '@/domain/impact-env/actions/get-impact-env-questions.action';
import { getImpactEnvResponses } from '@/domain/impact-env/actions/get-impact-env-responses.action';
import { getImpactEssQuestions } from '@/domain/impact-ess/actions/get-impact-ess-questions.action';
import { getImpactEssResponses } from '@/domain/impact-ess/actions/get-impact-ess-responses.action';
import { getImpactReferences } from '@/domain/impact-references/actions/get-impact-references.action';
import { getOrganisation } from '@/domain/organisation/actions/get-organisation.action';
import { ExportType } from '@/domain/pdf-reports/pdf-reports.type';
import { getProjectFestival } from '@/domain/project-festival/actions/get-project-festival.action';
import { getProjectVenue } from '@/domain/project-venue/actions/get-project-venue.action';
import { getVenueDates } from '@/domain/project-venue-dates/actions/get-venue-dates.action';
import { getVenuePresets } from '@/domain/project-venue-presets/actions/get-venue-presets.action';
import { store } from '@/domain/store';
import { Error } from '@/ui/pages/error/Error';

import { ScoreCardsFestivalC1 } from './cards/ProjectFestivalC1';
import { ScoreCardsFestivalC2 } from './cards/ProjectFestivalC2';
import { ScoreCardsFestivalC3 } from './cards/ProjectFestivalC3';
import { ScoreCardsFestivalC4 } from './cards/ProjectFestivalC4';
import { ScoreCardsVenueC1 } from './cards/ProjectVenueC1';
import { ScoreCardsVenueC2 } from './cards/ProjectVenueC2';
import { ScoreCardsVenueC3 } from './cards/ProjectVenueC3';
import { ScoreCardsVenueC4 } from './cards/ProjectVenueC4';

const ScoreCardsProjectFestival = () => {
  const location = useLocation();
  const hash = location.hash;

  switch (hash) {
    case '#card1':
      return <ScoreCardsFestivalC1 />;
    case '#card2':
      return <ScoreCardsFestivalC2 />;
    case '#card3':
      return <ScoreCardsFestivalC3 />;
    case '#card4':
      return <ScoreCardsFestivalC4 />;
    default:
      return <ScoreCardsFestivalC1 />;
  }
};

const ScoreCardsProjectVenue = () => {
  const location = useLocation();
  const hash = location.hash;

  switch (hash) {
    case '#card1':
      return <ScoreCardsVenueC1 />;
    case '#card2':
      return <ScoreCardsVenueC2 />;
    case '#card3':
      return <ScoreCardsVenueC3 />;
    case '#card4':
      return <ScoreCardsVenueC4 />;
    default:
      return <ScoreCardsVenueC1 />;
  }
};

export const scoreCardsRouter = createBrowserRouter([
  {
    path: '/score-cards',
    errorElement: <Error />,
    loader: assertUserIsConnected,
    children: [
      {
        path: 'projects/festival/:id',
        element: <ScoreCardsProjectFestival />,
        loader: pdfFestivalLoader,
      },
      {
        path: 'projects/venue/:id',
        element: <ScoreCardsProjectVenue />,
        loader: pdfVenueLoader,
      },
    ],
  },
]);

export async function assertUserIsConnected() {
  await store.dispatch(checkUserIsConnectedOnStart()).unwrap();
  return null;
}

export async function pdfFestivalLoader({
  params,
  request,
}: LoaderFunctionArgs) {
  store.dispatch(getOrganisation());
  const projectId = parseInt(params.id as string);
  const [, searchParams] = request.url.split('?');
  const exportType = searchParams
    ? new URLSearchParams(searchParams).get('export_type')
    : null;

  await store.dispatch(getProjectFestival(projectId));
  await store.dispatch(getImpactReferences({ projectId }));
  await store.dispatch(runImpactCalculateFestivalCo2ForProject({ projectId }));
  await store.dispatch(runImpactCalculateFestivalEnvForProject({ projectId }));
  await store.dispatch(runImpactCalculateFestivalEssForProject({ projectId }));

  if (exportType === ExportType.FULL_ANSWER_AND_RESULTS) {
    await store.dispatch(getImpactCo2Answers({ projectId }));
    await store.dispatch(getImpactEssQuestions({}));
    await store.dispatch(getImpactEnvQuestions());
    await store.dispatch(getImpactEssResponses({ projectId }));
    await store.dispatch(getImpactEnvResponses({ projectId }));
  }
  return null;
}

export async function pdfVenueLoader({ params, request }: LoaderFunctionArgs) {
  store.dispatch(getOrganisation());
  const projectId = parseInt(params.id as string);
  const [, searchParams] = request.url.split('?');
  const exportType = searchParams
    ? new URLSearchParams(searchParams).get('export_type')
    : null;

  await store.dispatch(getProjectVenue(projectId));
  await store.dispatch(getVenueDates());
  await store.dispatch(getVenuePresets());
  await store.dispatch(getImpactReferences({ projectId }));

  await Promise.all([
    store.dispatch(getImpactCalculateVenueCo2ForProject({ projectId })),
    // Hack to get the ESS_3 question for the PDF page 4.
    store.dispatch(getImpactEssResponses({ projectId })),
  ]);

  if (exportType === ExportType.FULL_ANSWER_AND_RESULTS) {
    await store.dispatch(getImpactCo2Answers({ projectId }));
    await store.dispatch(getImpactEssQuestions({}));
    await store.dispatch(getImpactEnvQuestions());
    await store.dispatch(getImpactEssResponses({ projectId }));
    await store.dispatch(getImpactEnvResponses({ projectId }));
  }
  return null;
}
